import React from 'react';
import { Card } from "react-bootstrap";
import { Field, ErrorMessage } from 'formik';
import CommonLabFields from '../sections/CommonLabFields';
import CommonFinalFields from '../sections/CommonFinalFields';

/**
 * TestResults Component - Handles all test result fields and logic
 * Extracted from NikshayTest for better separation of concerns
 * 
 * @param {Object} props - Component props
 * @param {Object} props.values - Formik values object
 * @param {Function} props.setFieldValue - Formik setFieldValue function
 * @param {string} props.testType - Current test type
 * @param {string} props.resultAvailability - Result availability status ('Present' or 'Absent')
 * @param {string} props.className - Additional CSS classes
 */
const TestResults = ({
  values,
  setFieldValue,
  testType = "",
  resultAvailability = "",
  className = ""
}) => {
  // Test types that use text result input
  const TEST_TYPES_CYTOPATH = [
    'Cytopathology',
    'Histopathology', 
    'Gene Sequencing',
    'Pathodetect - MTB, Rif & Inh'
  ];

  // FLLPA mutation field constants
  const RpoB_WT_FIELDS = ['RpoB_WT1', 'RpoB_WT2', 'WT3', 'WT4', 'WT5', 'WT6', 'WT7', 'WT8'];
  const RpoB_MUT_FIELDS = [
    { name: 'MUT1_D516V', label: 'MUT1 (D516V)' }, 
    { name: 'MUT2A_H526Y', label: 'MUT2A (H526Y)' },
    { name: 'MUT2B_H526D', label: 'MUT2B (H526D)' },
    { name: 'MUT3_S513L', label: 'MUT3 (S513L)' }
  ];
  const INH_WT_MUT_FIELDS = ['INH_WT1', 'INH_WT2', 'INH_MUT1', 'INH_MUT2', 'MUT3A', 'MUT3B'];
  const KAT_WT_MUT_FIELDS = ['KAT_WT1', 'KAT_MUT1', 'KAT_MUT2'];

  // Helper function to get FLLPA Final Interpretation options
  const getFLLPAFinalInterpretationOptions = (resultValue) => {
    switch (resultValue) {
      case 'M.Tb. not detected':
        return [
          { key: "M.Tb. not detected", value: "M.Tb. not detected", label: "M.Tb. not detected" }
        ];
      case 'M.Tb. detected':
        return [
          { key: "M.Tb. detected", value: "M.Tb. detected", label: "M.Tb. detected" }
        ];
      case 'Invalid':
        return [
          { key: "Invalid", value: "Invalid", label: "Invalid" }
        ];
      case 'Indeterminate':
        return [
          { key: "Indeterminate", value: "Indeterminate", label: "Indeterminate" }
        ];
      case 'Contaminated':
        return [
          { key: "Contaminated", value: "Contaminated", label: "Contaminated" }
        ];
      default:
        return [];
    }
  };

  // Don't render if Result Availability is not Present
  if (resultAvailability !== 'Present') {
    return null;
  }

  return (
    <Card className={`my-4 ${className}`}>
      <Card.Header className="bg-light p-3 mb-3">
        <h4 className="fw-bold mb-0 text-primary">Test Results</h4>
      </Card.Header>
      <Card.Body className="mb-2">
        {/* SCENARIO 1:
        When *Test Type selected as: 
        Chest X Ray
        or Microscopy ZN
        or Microscopy Fluorescent 
        or CBNAAT or Truenat (MTB) 
        or Truenat (MTB-RIF) 
        or Culture 
        is handled in Common Lab Fields
            */}
        {/* Common Lab Fields - Always shown first */}
        <CommonLabFields values={values} setFieldValue={setFieldValue} testType={testType} />

        {/* FLLPA Test Results */}
        {testType === 'FLLPA' && (
          <>
            {/* Add Mutation Details Control Field */}
            <div className="row mt-4">
              <div className="col-md-6">
                <div className="form-group radio-container">
                  <label className="col-sm-4">
                    <span style={{ color: "red", fontSize: "20px" }}>*</span>Add Mutation Details:
                  </label>
                  <div className="radio-buttons col-sm-5">
                    <label className="">
                      <Field type="radio" name="FLLPA_AddMutationDetails" value="Add" /> Add
                    </label>
                    <label className="ms-3">
                      <Field 
                        type="radio" 
                        name="FLLPA_AddMutationDetails" 
                        value="Skip"
                        onClick={() => {
                          // Clear all FLLPA mutation details when Skip is selected
                          setFieldValue('FLLPA_AddMutationDetails', 'Skip'); 
                          setFieldValue('FLLPA_TUB_BAND', null);
                          setFieldValue('FLLPA_Rpob_Locus_Control', null);
                          setFieldValue('FLLPA_INH_A', null);
                          setFieldValue('FLLPA_KAT_G', null);
                        }}
                      /> Skip
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            {/* FLLPA Mutation Details - Show only if Add is selected */}
            {values.FLLPA_AddMutationDetails === 'Add' && (
              <>
                <div className="row mt-3">
                  {/* Sample Type */}
                  <div className="col-md-6">
                    <div className="form-group radio-container">
                      <label className="col-sm-4">
                        <span className="required-asterisk">*</span>Sample:
                      </label>
                      <div className="radio-buttons col-sm-5">
                        <label className="">
                          <Field type="radio" name="FLLPASample" value="Direct" /> Direct
                        </label>
                        <label className="ms-3">
                          <Field type="radio" name="FLLPASample" value="Indirect" /> Indirect
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* TUB Band */}
                  <div className="col-md-6">
                    <div className="form-group radio-container">
                      <label className="col-sm-4">
                        <span className="required-asterisk">*</span>TUB Band:
                      </label>
                      <div className="radio-buttons col-sm-5">
                        <label className="">
                          <Field type="radio" name="FLLPA_TUB_BAND" value="Present" /> Present
                        </label>
                        <label className="ms-3">
                          <Field type="radio" name="FLLPA_TUB_BAND" value="Absent" /> Absent
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* TUB Band Present - Show Rifampicin and Isoniazid mutations */}
                {values.FLLPA_TUB_BAND === 'Present' && (
                  <>
                    {/* RpoB Locus Control */}
                    <div className="row mt-3 w-100 ms-0">
                      <div className="col-md-6">
                        <div className="form-group radio-container">
                          <label className="col-sm-4">
                            <span className="required-asterisk">*</span>RpoB Locus Control:
                          </label>
                          <div className="radio-buttons col-sm-5">
                            <label className="">
                              <Field type="radio" name="FLLPA_Rpob_Locus_Control" value="Present" /> Present
                            </label>
                            <label className="ms-3">
                              <Field type="radio" name="FLLPA_Rpob_Locus_Control" value="Absent" /> Absent
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* RpoB WT/MUT Fields - Show only if RpoB Locus Control is Present */}
                    {values.FLLPA_Rpob_Locus_Control === 'Present' && (
                      <div className="row mt-3">
                        {RpoB_WT_FIELDS.map((field, index) => (
                          <div className="col-md-6 mt-3" key={`RpoB_WT_${index}`}>
                            <div className="form-group radio-container">
                              <label className="col-sm-4">
                                <span className="required-asterisk">*</span>
                                {field.includes('RpoB_') ? field.replace('RpoB_', '') : field}:
                              </label>
                              <div className="radio-buttons col-sm-5">
                                <label className="">
                                  <Field type="radio" name={`FLLPA_${field}`} value="Present" /> Present
                                </label>
                                <label className="ms-3">
                                  <Field type="radio" name={`FLLPA_${field}`} value="Absent" /> Absent
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                        {RpoB_MUT_FIELDS.map((item, index) => (
                          <div className="col-md-6 mt-3" key={`RpoB_MUT_${index}`}>
                            <div className="form-group radio-container">
                              <label className="col-sm-4">
                                <span className="required-asterisk">*</span>{item.label}:
                              </label>
                              <div className="radio-buttons col-sm-5">
                                <label className="">
                                  <Field type="radio" name={`FLLPA_${item.name}`} value="Present" /> Present
                                </label>
                                <label className="ms-3">
                                  <Field type="radio" name={`FLLPA_${item.name}`} value="Absent" /> Absent
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Isoniazid Path - Inh A Locus Control */}
                    <div className="row mt-3 w-100 ms-0">
                      <div className="col-md-6">
                        <div className="form-group radio-container">
                          <label className="col-sm-4">
                            <span className="required-asterisk">*</span>Inh A:
                          </label>
                          <div className="radio-buttons col-sm-5">
                            <label className="">
                              <Field type="radio" name="FLLPA_INH_A" value="Present" /> Present
                            </label>
                            <label className="ms-3">
                              <Field type="radio" name="FLLPA_INH_A" value="Absent" /> Absent
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Inh A WT/MUT Fields - Show only if Inh A is Present */}
                    {values.FLLPA_INH_A === 'Present' && (
                      <div className="row mt-3">
                        {INH_WT_MUT_FIELDS.map((field, index) => (
                          <div className="col-md-6 mt-3" key={`Inh_WT_MUT_${index}`}>
                            <div className="form-group radio-container">
                              <label className="col-sm-4">
                                <span className="required-asterisk">*</span>
                                {field.replace('INH_', 'Inh ').replace('_', ' ')}:
                              </label>
                              <div className="radio-buttons col-sm-5">
                                <label className="">
                                  <Field type="radio" name={`FLLPA_${field}`} value="Present" /> Present
                                </label>
                                <label className="ms-3">
                                  <Field type="radio" name={`FLLPA_${field}`} value="Absent" /> Absent
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Kat G Locus Control */}
                    <div className="row mt-3 w-100 ms-0">
                      <div className="col-md-6">
                        <div className="form-group radio-container">
                          <label className="col-sm-4">
                            <span className="required-asterisk">*</span>Kat G:
                          </label>
                          <div className="radio-buttons col-sm-5">
                            <label className="">
                              <Field type="radio" name="FLLPA_KAT_G" value="Present" /> Present
                            </label>
                            <label className="ms-3">
                              <Field type="radio" name="FLLPA_KAT_G" value="Absent" /> Absent
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Kat G WT/MUT Fields - Show only if Kat G is Present */}
                    {values.FLLPA_KAT_G === 'Present' && (
                      <div className="row mt-3">
                        {KAT_WT_MUT_FIELDS.map((field, index) => {
                          const label = field.includes('KAT_') ? field.replace('KAT_', '') : field;
                          return (
                            <div className="col-md-6 mt-3" key={`Kat_WT_MUT_${index}`}>
                              <div className="form-group radio-container">
                                <label className="col-sm-4">
                                  <span className="required-asterisk">*</span>{label}:
                                </label>
                                <div className="radio-buttons col-sm-5">
                                  <label className="">
                                    <Field type="radio" name={`FLLPA_${field}`} value="Present" /> Present
                                  </label>
                                  <label className="ms-3">
                                    <Field type="radio" name={`FLLPA_${field}`} value="Absent" /> Absent
                                  </label>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* FLLPA Result and Resistance Fields */}
            <div className="row mt-3">
              {/* FLLPA Result */}
              <div className="col-md-6">
                <div className="form-group">
                  <label>
                    <span className="required-asterisk">*</span>Result
                  </label>
                  <Field 
                    as="select" 
                    id="FLLPA_Result" 
                    name="FLLPA_Result" 
                    className="form-control input-width select"
                  >
                    <option value="">Select FLLPA Result</option>
                    <option value="M.Tb. not detected">M.Tb. not detected</option>
                    <option value="M.Tb. detected">M.Tb. detected</option>
                    <option value="Invalid">Invalid</option>
                    <option value="Indeterminate">Indeterminate</option>
                    <option value="Contaminated">Contaminated</option>
                  </Field>
                </div>
              </div>

              {/* Resistance Fields - Show only if Result is 'M.Tb. detected' */}
              {values.FLLPA_Result === 'M.Tb. detected' && (
                <>
                  {/* Resistance to Rifampicin */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>
                        <span className="required-asterisk">*</span>Resistance to Rifampicin (RpoB)
                      </label>
                      <Field 
                        as="select" 
                        id="ResistanceToRifampicin" 
                        name="ResistanceToRifampicin" 
                        className="form-control input-width select"
                      >
                        <option value="">Select Resistance To Rifampicin</option>
                        <option value="Detected">Detected</option>
                        <option value="Inferred">Inferred</option>
                        <option value="Not detected">Not detected</option>
                      </Field>
                    </div>
                  </div>

                  {/* Low Level Resistance to Isoniazid */}
                  <div className="col-md-6 mt-3">
                    <div className="form-group">
                      <label>
                        <span className="required-asterisk">*</span>Low Level Resistance to Isoniazid (Inh A)
                      </label>
                      <Field 
                        as="select" 
                        id="LowLevelResistanceToIsoniazid" 
                        name="LowLevelResistanceToIsoniazid" 
                        className="form-control input-width select"
                      >
                        <option value="">Select Low Level Resistance To Isoniazid</option>
                        <option value="Detected">Detected</option>
                        <option value="Inferred">Inferred</option>
                        <option value="Not detected">Not detected</option>
                      </Field>
                    </div>
                  </div>

                  {/* High Level Resistance to Isoniazid */}
                  <div className="col-md-6 mt-3">
                    <div className="form-group">
                      <label>
                        <span className="required-asterisk">*</span>High Level Resistance to Isoniazid (Kat G)
                      </label>
                      <Field 
                        as="select" 
                        id="HighLevelResistanceToIsoniazid" 
                        name="HighLevelResistanceToIsoniazid" 
                        className="form-control input-width select"
                      >
                        <option value="">Select High Level Resistance To Isoniazid</option>
                        <option value="Detected">Detected</option>
                        <option value="Inferred">Inferred</option>
                        <option value="Not detected">Not detected</option>
                      </Field>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* SLLPA Test Results */}
        {testType === 'SLLPA' && (
          <>
            {/* Add Mutation Details Control */}
            <div className="row mt-4">
              <div className="col-md-6">
                <div className="form-group radio-container">
                  <label className="col-sm-4">
                   <span style={{ color: "red", fontSize: "20px" }}>*</span>Add Mutation Details:
                  </label>
                  <div className="radio-buttons col-sm-5">
                    <label className="">
                      <Field 
                        type="radio" 
                        name="SLLPA_AddMutationDetails" 
                        value="Add"
                        onClick={() => {
                          setFieldValue('SLLPA_AddMutationDetails', 'Add');
                        }}
                      /> Add
                    </label>
                    <label className="">
                      <Field 
                        type="radio" 
                        name="SLLPA_AddMutationDetails" 
                        value="Skip"
                        onClick={() => {
                          // Clear all SLLPA mutation details when Skip is selected
                          setFieldValue('SLLPA_AddMutationDetails', 'Skip');
                          setFieldValue('GYR_A_Locus_Control', null);
                          setFieldValue('GYR_B_Locus_Control', null);
                          setFieldValue('RRS_Locus_Control', null);
                          setFieldValue('EIS_Locus_Control', null);
                        }}
                      /> Skip
                    </label>
                  </div>
                  <ErrorMessage name="SLLPA_AddMutationDetails" component="div" className="text-danger" />
                </div>
              </div>
            </div>

            {/* SLLPA Mutation Details - Show only if Add is selected */}
            {values.SLLPA_AddMutationDetails === 'Add' && (
              <div className="row border p-3 mt-3">
                <h5 className="col-12 mb-3">Mutation Details</h5>

                {/* gyr A Locus Control */}
                <div className="col-md-6 mt-3">
                  <div className="form-group radio-container">
                    <label className="col-sm-4">
                      <span>*</span>gyr A Locus Control:
                    </label>
                    <div className="radio-buttons col-sm-5">
                      <label><Field type="radio" name="GYR_A_Locus_Control" value="Present" /> Present </label>
                      <label><Field type="radio" name="GYR_A_Locus_Control" value="Absent" /> Absent </label>
                    </div>
                    <ErrorMessage name="GYR_A_Locus_Control" component="div" className="text-danger" />
                  </div>
                </div>

                {/* Gyr A Mutations - Show only if gyr A Locus Control is Present */}
                {values.GYR_A_Locus_Control === 'Present' && (
                  <>
                    {/* WT1 (85-90) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT1 (85-90):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT1_85_90" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT1_85_90" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT1_85_90" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* WT2 (89-93) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT2 (89-93):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT2_89_93" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT2_89_93" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT2_89_93" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* WT3 (92-97) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT3 (92-97):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT3_92_97" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT3_92_97" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT3_92_97" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT1 (A90V) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT1 (A90V):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT1_A90V" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT1_A90V" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT1_A90V" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT2 (S91P) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT2 (S91P):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT2_S91P" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT2_S91P" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT2_S91P" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT3A (D94A) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT3A (D94A):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT3A_D94A" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT3A_D94A" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT3A_D94A" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT3B (D94N/Y) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT3B (D94N/Y):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT3B_D94N" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT3B_D94N" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT3B_D94N" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT3C (D94G) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT3C (D94G):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT3C_D94G" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT3C_D94G" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT3C_D94G" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT3D (D94H) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT3D (D94H):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT3D_D94H" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT3D_D94H" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT3D_D94H" component="div" className="text-danger" />
                      </div>
                    </div>
                  </>
                )}

                {/* gyr B Locus Control */}
                <div className="col-md-6 mt-3">
                  <div className="form-group radio-container">
                    <label className="col-sm-4">
                      <span>*</span>gyr B Locus Control:
                    </label>
                    <div className="radio-buttons col-sm-5">
                      <label><Field type="radio" name="GYR_B_Locus_Control" value="Present" /> Present </label>
                      <label><Field type="radio" name="GYR_B_Locus_Control" value="Absent" /> Absent </label>
                    </div>
                    <ErrorMessage name="GYR_B_Locus_Control" component="div" className="text-danger" />
                  </div>
                </div>

                {/* Gyr B Mutations - Show only if gyr B Locus Control is Present */}
                {values.GYR_B_Locus_Control === 'Present' && (
                  <>
                    {/* WT1 (536-541) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT1 (536-541):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT1_536_541" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT1_536_541" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT1_536_541" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT1 (N538D) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT1 (N538D):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT1_N538D" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT1_N538D" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT1_N538D" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT2 (E540V) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT2 (E540V):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT2_E540V" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT2_E540V" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT2_E540V" component="div" className="text-danger" />
                      </div>
                    </div>
                  </>
                )}

                {/* rrs Locus Control */}
                <div className="col-md-6 mt-3">
                  <div className="form-group radio-container">
                    <label className="col-sm-4">
                      <span>*</span>rrs Locus Control:
                    </label>
                    <div className="radio-buttons col-sm-5">
                      <label><Field type="radio" name="RRS_Locus_Control" value="Present" /> Present </label>
                      <label><Field type="radio" name="RRS_Locus_Control" value="Absent" /> Absent </label>
                    </div>
                    <ErrorMessage name="RRS_Locus_Control" component="div" className="text-danger" />
                  </div>
                </div>

                {/* rrs Mutations - Show only if rrs Locus Control is Present */}
                {values.RRS_Locus_Control === 'Present' && (
                  <>
                    {/* WT1 (1401-02) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT1 (1401-02):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT1_1401_02" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT1_1401_02" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT1_1401_02" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* WT2 (1484) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT2 (1484):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT2_1484" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT2_1484" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT2_1484" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT1 (A1401G) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT1 (A1401G):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT1_A1401G" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT1_A1401G" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT1_A1401G" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT2 (G148T) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT2 (G148T):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT2_G148T" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT2_G148T" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT2_G148T" component="div" className="text-danger" />
                      </div>
                    </div>
                  </>
                )}

                {/* eis Locus Control */}
                <div className="col-md-6 mt-3">
                  <div className="form-group radio-container">
                    <label className="col-sm-4">
                      <span>*</span>eis Locus Control:
                    </label>
                    <div className="radio-buttons col-sm-5">
                      <label><Field type="radio" name="EIS_Locus_Control" value="Present" /> Present </label>
                      <label><Field type="radio" name="EIS_Locus_Control" value="Absent" /> Absent </label>
                    </div>
                    <ErrorMessage name="EIS_Locus_Control" component="div" className="text-danger" />
                  </div>
                </div>

                {/* eis Mutations - Show only if eis Locus Control is Present */}
                {values.EIS_Locus_Control === 'Present' && (
                  <>
                    {/* WT1 (37) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT1 (37):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT1_37" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT1_37" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT1_37" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* WT2 (14, 12, 10) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT2 (14, 12, 10):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT2_14_12_10" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT2_14_12_10" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT2_14_12_10" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* WT3 (2) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>WT3 (2):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_WT3_2" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_WT3_2" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_WT3_2" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* MUT1 (C-14T) */}
                    <div className="col-md-6 mt-3">
                      <div className="form-group radio-container">
                        <label className="col-sm-4"><span>*</span>MUT1 (C-14T):</label>
                        <div className="radio-buttons col-sm-5">
                          <label><Field type="radio" name="SLLPA_MUT1_C_14T" value="Present" /> Present </label>
                          <label><Field type="radio" name="SLLPA_MUT1_C_14T" value="Absent" /> Absent </label>
                        </div>
                        <ErrorMessage name="SLLPA_MUT1_C_14T" component="div" className="text-danger" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* SLLPA Result */}
            <div className="row mt-3">
              <div className="col-md-6">
                <div className="form-group">
                  <label>
                    <span className="text-danger-asterisk">*</span>Result
                  </label>
                  <Field 
                    as="select" 
                    id="SLLPA_Result" 
                    name="SLLPA_Result" 
                    className="form-control input-width select"
                  >
                    <option value="">Select SLLPA Result</option>
                    <option value="M.Tb. not detected">M.Tb. not detected</option>
                    <option value="M.Tb. detected">M.Tb. detected</option>
                    <option value="Invalid">Invalid</option>
                    <option value="Indeterminate">Indeterminate</option>
                    <option value="Contaminated">Contaminated</option>
                  </Field>
                  <ErrorMessage name="SLLPA_Result" component="div" className="text-danger" />
                </div>
              </div>

              {/* SLLPA Resistance Fields - Show only if Result is 'M.Tb. detected' */}
              {values.SLLPA_Result === 'M.Tb. detected' && (
                <div className="row mt-3">
                  {/* Resistance to Fluoroquinolones */}
                  <div className="col-md-6 mt-3">
                    <div className="form-group">
                      <label>
                        <span className="required-asterisk">*</span>Resistance to Fluoroquinolones: (gyr A, gyr B)
                      </label>
                      <Field 
                        as="select" 
                        id="ResistanceToFluoroquinolones" 
                        name="ResistanceToFluoroquinolones" 
                        className="form-control input-width select"
                      >
                        <option value="">Select Resistance To Fluoroquinolones</option>
                        <option value="Detected">Detected</option>
                        <option value="Detected(Mut 3B, 3C, 3D)">Detected(Mut 3B, 3C, 3D)</option>
                        <option value="Inferred">Inferred</option>
                        <option value="Not detected">Not detected</option>
                      </Field>
                    </div>
                  </div>

                  {/* Resistance to Second Line Injectable Drugs (rrs) */}
                  <div className="col-md-6 mt-3">
                    <div className="form-group">
                      <label>
                        <span className="required-asterisk">*</span>Resistance to Second Line Injectable Drugs: (Km, Am and Cm) (rrs)
                      </label>
                      <Field 
                        as="select" 
                        id="ResistanceToSecondLineInjectableDrugs" 
                        name="ResistanceToSecondLineInjectableDrugs" 
                        className="form-control input-width select"
                      >
                        <option value="">Select Resistance To Second Line Injectable Drugs</option>
                        <option value="Detected">Detected</option>
                        <option value="Inferred">Inferred</option>
                        <option value="Inferred* (1402 mutation)">Inferred* (1402 mutation)</option>
                        <option value="Not detected">Not detected</option>
                      </Field>
                    </div>
                  </div>

                  {/* Resistance to Second Line Injectable Drugs: (Low Level Km) (eis) */}
                  <div className="col-md-6 mt-3">
                    <div className="form-group">
                      <label>
                        <span className="required-asterisk">*</span>Resistance to Second Line Injectable Drugs: (Low Level Km) (eis)
                      </label>
                      <Field 
                        as="select" 
                        id="ResistanceToSecondLineInjectableDrugsLowLevel" 
                        name="ResistanceToSecondLineInjectableDrugsLowLevel" 
                        className="form-control input-width select"
                      >
                        <option value="">Select Resistance To Second Line Injectable Drugs Low Level</option>
                        <option value="Detected">Detected</option>
                        <option value="Inferred">Inferred</option>
                        <option value="Not detected">Not detected</option>
                      </Field>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* DST Test Results */}
        {testType === 'DST' && (
          <>
            <div className="row mt-4">
              {/* Culture Type - Required */}
              <div className="col-md-6">
                <div className="form-group radio-container required-field">
                  <label className="col-sm-4 control-label">
                    <span className="required-asterisk">*</span>Culture Type:
                  </label>
                  <div className="radio-buttons col-sm-8">
                    <label className="mr-3">
                      <Field type="radio" name="CultureType" value="LJ Culture Result" /> LJ Culture Result
                    </label>
                    <label>
                      <Field type="radio" name="CultureType" value="Liquid Culture Result (MGIT)" /> Liquid Culture Result (MGIT)
                    </label>
                  </div>
                  <ErrorMessage name="CultureType" component="div" className="text-danger" />
                </div>
              </div>

              {/* DST To Drug - Required */}
              <div className="col-md-6">
                <div className="form-group required-field">
                  <label htmlFor="DSTToDrug">
                    <span className="required-asterisk">*</span>DST To Drug
                  </label>
                  <Field 
                    as="select" 
                    id="DSTToDrug" 
                    name="DSTToDrug" 
                    className="form-control input-width select"
                  >
                    <option value="">Select DST To Drug</option>
                    <option value="H">H</option>
                    <option value="R">R</option>
                    <option value="E">E</option>
                    <option value="Z">Z</option>
                    <option value="S">S</option>
                    <option value="Am">Am</option>
                    <option value="Km">Km</option>
                    <option value="Cm">Cm</option>
                    <option value="Lfx">Lfx</option>
                    <option value="Mfx(1.0)">Mfx(1.0)</option>
                    <option value="Lzd">Lzd</option>
                    <option value="Eto">Eto</option>
                    <option value="PAS">PAS</option>
                    <option value="Cfz">Cfz</option>
                    <option value="Dlm">Dlm</option>
                    <option value="BDQ">BDQ</option>
                  </Field>
                  <ErrorMessage name="DSTToDrug" component="div" className="text-danger" />
                </div>
              </div>
            </div>

            <div className="row mt-3">
              {/* Resistance Status - Required */}
              <div className="col-md-6">
                <div className="form-group required-field">
                  <label htmlFor="ResistanceStatus">
                    <span className="required-asterisk">*</span>Resistance Status
                  </label>
                  <Field 
                    as="select" 
                    id="ResistanceStatus" 
                    name="ResistanceStatus" 
                    className="form-control input-width select"
                  >
                    <option value="">Select Resistance Status</option>
                    <option value="Susceptible">Susceptible</option>
                    <option value="Resistant">Resistant</option>
                    <option value="None">None</option>
                  </Field>
                  <ErrorMessage name="ResistanceStatus" component="div" className="text-danger" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Cytopathology, Histopathology, Gene Sequencing, Pathodetect Results */}
        {TEST_TYPES_CYTOPATH.includes(testType) && (
          <div className="row mt-4">
            <div className="col-md-6">
              <div className="form-group">
                <label>
                  <span className="required-asterisk">*</span>Result
                </label>
                <Field 
                  type="text" 
                  id="TextResult" 
                  name="TextResult" 
                  className="form-control input-width"
                  placeholder="Enter test result"
                />
                <ErrorMessage name="TextResult" component="div" className="text-danger" />
              </div>
            </div>
          </div>
        )}

        {/* Other Test Type Results */}
        {testType === 'Other' && (
          <div className="row mt-4">
            <div className="col-md-6">
              <div className="form-group">
                <label>
                  <span className="required-asterisk">*</span>Result
                </label>
                <Field 
                  type="text" 
                  id="TextResult" 
                  name="TextResult" 
                  placeholder="Enter Result" 
                  className="form-control input-width"
                />
                <ErrorMessage name="TextResult" component="div" className="text-danger" />
              </div>
            </div>
          </div>
        )}

        {/* Common Final Fields - Always shown last */}
        <CommonFinalFields 
          testType={testType} 
          values={values} 
          setFieldValue={setFieldValue}
          customInterpretationOptions={
            testType === 'FLLPA' 
              ? getFLLPAFinalInterpretationOptions(values.FLLPA_Result)
              : null
          }
        />

      </Card.Body>
    </Card>
  );
};

export default TestResults;